/**
 * DTX - Mobile app bottom tab bar.
 * Menu: controls header-drawer directly (no summary.click race).
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const SELECTOR = '[data-dtx-app-tab-bar]';
  const MENU_LOCK_MS = 280;

  /** @type {boolean} */
  let menuBusy = false;

  /** @returns {HTMLDetailsElement | null} */
  function getMenuDetails() {
    const el = document.getElementById('Details-menu-drawer-container');
    return el instanceof HTMLDetailsElement ? el : null;
  }

  /** @returns {HTMLElement | null} */
  function getHeaderDrawer() {
    const el = document.getElementById('header-menu-drawer');
    return el instanceof HTMLElement ? el : null;
  }

  function isMenuOpen() {
    const details = getMenuDetails();
    return Boolean(details && (details.hasAttribute('open') || details.classList.contains('menu-open')));
  }

  function syncMenuTabState() {
    const open = isMenuOpen();
    document.documentElement.classList.toggle('dtx-menu-open', open);

    const menuButton = document.querySelector('[data-dtx-tab-menu]');
    if (!menuButton) return;

    menuButton.classList.toggle('is-active', open);
    menuButton.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * Open/close via header-drawer API + explicit details state.
   * Avoids summary.click() races with native <details> toggle and on:click="/toggle".
   */
  async function toggleAppMenu() {
    if (menuBusy) return;

    const details = getMenuDetails();
    if (!details) return;

    menuBusy = true;

    try {
      let drawer = getHeaderDrawer();

      if (drawer && !customElements.get('header-drawer')) {
        try {
          await customElements.whenDefined('header-drawer');
        } catch {
          // Keep going with a manual fallback below.
        }
        drawer = getHeaderDrawer();
      }

      if (drawer) {
        customElements.upgrade(drawer);
      }

      const shouldClose = isMenuOpen();

      if (shouldClose) {
        if (drawer && typeof /** @type {any} */ (drawer).close === 'function') {
          /** @type {any} */ (drawer).close();
        } else {
          details.classList.remove('menu-open');
          details.removeAttribute('open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      } else {
        // Ensure content is in the DOM before theme animation/focus trap.
        details.setAttribute('open', '');

        if (drawer && typeof /** @type {any} */ (drawer).open === 'function') {
          /** @type {any} */ (drawer).open();
        } else {
          details.classList.add('menu-open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
        }
      }

      syncMenuTabState();
    } finally {
      window.setTimeout(() => {
        menuBusy = false;
        syncMenuTabState();
      }, MENU_LOCK_MS);
    }
  }

  /** @type {MutationObserver | null} */
  let menuObserver = null;

  function bindMenuObserver() {
    const details = getMenuDetails();
    if (!details) return;

    menuObserver?.disconnect();
    menuObserver = new MutationObserver(syncMenuTabState);
    menuObserver.observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
    syncMenuTabState();
  }

  /**
   * @param {HTMLElement} tabBar
   */
  function syncCartBubble(tabBar) {
    const sourceCount = document.querySelector('[data-testid="cart-icon"] [data-testid="cart-bubble"]');
    const targetBubble = tabBar.querySelector('[data-dtx-tab-cart] .cart-bubble');
    const targetCount = tabBar.querySelector('[data-dtx-tab-cart] [data-testid="cart-bubble"]');

    if (!sourceCount || !targetBubble || !targetCount) return;

    const count = sourceCount.textContent?.trim() || '';
    const hasItems = count !== '' && count !== '0';

    targetBubble.classList.toggle('visually-hidden', !hasItems);
    targetCount.classList.toggle('hidden', !hasItems);
    targetCount.textContent = count;
  }

  async function getCartDrawer() {
    if (customElements.get('theme-drawer')) {
      return document.getElementById('cart-drawer');
    }

    try {
      await customElements.whenDefined('theme-drawer');
    } catch {
      return document.getElementById('cart-drawer');
    }

    return document.getElementById('cart-drawer');
  }

  async function toggleAppCart() {
    const cartDrawer = await getCartDrawer();

    if (cartDrawer && typeof /** @type {any} */ (cartDrawer).toggle === 'function') {
      /** @type {any} */ (cartDrawer).toggle();
      return;
    }

    document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
  }

  /**
   * @param {HTMLElement} tabBar
   */
  function initTabBar(tabBar) {
    if (tabBar.dataset.dtxBound === 'true') {
      bindMenuObserver();
      syncCartBubble(tabBar);
      return;
    }
    tabBar.dataset.dtxBound = 'true';

    bindMenuObserver();

    tabBar.querySelector('[data-dtx-tab-menu]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggleAppMenu();
    });

    const cartButton = tabBar.querySelector('[data-dtx-tab-cart][type="button"]');
    cartButton?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleAppCart();
    });

    syncCartBubble(tabBar);

    const cartIcon = document.querySelector('[data-testid="cart-icon"]');
    if (cartIcon && 'MutationObserver' in window) {
      new MutationObserver(() => syncCartBubble(tabBar)).observe(cartIcon, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }
  }

  function boot() {
    if (!MOBILE_MQ.matches) return;

    const tabBar = document.querySelector(SELECTOR);
    if (tabBar instanceof HTMLElement) initTabBar(tabBar);
    else bindMenuObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  MOBILE_MQ.addEventListener('change', (event) => {
    if (event.matches) {
      boot();
      return;
    }

    if (isMenuOpen()) {
      const details = getMenuDetails();
      const drawer = getHeaderDrawer();
      if (drawer && typeof /** @type {any} */ (drawer).close === 'function') {
        /** @type {any} */ (drawer).close();
      } else if (details) {
        details.classList.remove('menu-open');
        details.removeAttribute('open');
      }
    }
    syncMenuTabState();
  });
})();
